import React, { useState, useEffect, useCallback, useMemo } from "react";
import "../home-styling/Reviews.css";

// Constants for better maintainability
const API_ENDPOINTS = {
  PACKAGES: '/api/packages',
  PACKAGE_REVIEWS: '/api/packages'
};

const DEFAULT_REVIEW_STATS = {
  total: 0,
  averageRating: 0,
  ratingDistribution: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 },
  categoryAverages: { itinerary: 0, value: 0, safety: 0, overall: 0 }
};

const Reviews = ({
  loading,
  getAuthHeaders,
  handleUnauthorized,
  formatDate,
  getOperatorId,
}) => {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(DEFAULT_REVIEW_STATS);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsError, setReviewsError] = useState(null);

  // Memoized utility functions
  const safeToFixed = useCallback((value, decimals = 1) => {
    const num = parseFloat(value);
    return isNaN(num) ? '0.0' : num.toFixed(decimals);
  }, []);

  const safeRound = useCallback((value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : Math.round(num);
  }, []);

  const safeParseFloat = useCallback((value) => {
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  }, []);

  const safeFormatDate = useCallback((dateString) => {
    if (!dateString) return "Not specified";
    try {
      return formatDate
        ? formatDate(dateString)
        : new Date(dateString).toLocaleDateString("en-IN");
    } catch (error) {
      return "Invalid date";
    }
  }, [formatDate]);

  // Memoized API functions
  const fetchOperatorPackages = useCallback(async () => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PACKAGES}`, {
      headers: getAuthHeaders(),
    });

    if (response.status === 401) {
      handleUnauthorized();
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to load packages: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success && result.data) return result.data;
    if (Array.isArray(result)) return result;
    if (result.packages) return result.packages;
    
    return [];
  }, [getAuthHeaders, handleUnauthorized]);

  const fetchPackageReviews = useCallback(async (packageId) => {
    const API_BASE_URL = process.env.REACT_APP_API_URL;
    const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.PACKAGE_REVIEWS}/${packageId}/reviews`, {
      headers: getAuthHeaders(),
    });
    
    if (response.ok) return response.json();
    return { success: false, data: [] };
  }, [getAuthHeaders]);

  // Memoized data processing
  const processReviewsData = useCallback((allReviews) => {
    if (allReviews.length === 0) {
      setReviews([]);
      setReviewStats(DEFAULT_REVIEW_STATS);
      return;
    }

    setReviews(allReviews);

    const total = allReviews.length;
    const categorySums = { itinerary: 0, value: 0, safety: 0, overall: 0 };
    const categoryCounts = { itinerary: 0, value: 0, safety: 0, overall: 0 };
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };

    allReviews.forEach((review) => {
      const overallRating = safeParseFloat(review.rating_overall);
      if (overallRating >= 1 && overallRating <= 5) {
        distribution[safeRound(overallRating)]++;
      }

      // Calculate category averages
      const categories = [
        { key: 'itinerary', value: review.rating_itinerary },
        { key: 'value', value: review.rating_value },
        { key: 'safety', value: review.rating_safety },
        { key: 'overall', value: review.rating_overall }
      ];

      categories.forEach(({ key, value }) => {
        if (value) {
          categorySums[key] += safeParseFloat(value);
          categoryCounts[key]++;
        }
      });
    });

    const categoryAverages = Object.keys(categorySums).reduce((acc, key) => {
      acc[key] = categoryCounts[key] > 0 
        ? categorySums[key] / categoryCounts[key] 
        : 0;
      return acc;
    }, {});

    setReviewStats({
      total,
      averageRating: parseFloat(safeToFixed(categoryAverages.overall)),
      ratingDistribution: distribution,
      categoryAverages: Object.keys(categoryAverages).reduce((acc, key) => {
        acc[key] = parseFloat(safeToFixed(categoryAverages[key]));
        return acc;
      }, {})
    });
  }, [safeParseFloat, safeRound, safeToFixed]);

  // Load reviews data
  const loadReviews = useCallback(async () => {
    try {
      setReviewsLoading(true);
      setReviewsError(null);

      const packagesData = await fetchOperatorPackages();
      if (!packagesData) return;

      if (packagesData.length === 0) {
        setReviews([]);
        setReviewStats(DEFAULT_REVIEW_STATS);
        return;
      }

      const packageIds = packagesData
        .map((pkg) => pkg.package_id || pkg.id)
        .filter(Boolean);

      if (packageIds.length === 0) {
        setReviews([]);
        setReviewStats(DEFAULT_REVIEW_STATS);
        return;
      }

      // Fetch reviews for all packages in parallel
      const reviewsResults = await Promise.all(
        packageIds.map(packageId => fetchPackageReviews(packageId))
      );

      // Combine all reviews with package information
      const allReviews = reviewsResults.reduce((acc, result, index) => {
        if (result.success && Array.isArray(result.data)) {
          const packageInfo = packagesData[index];
          const reviewsWithPackageInfo = result.data.map((review) => ({
            ...review,
            package_name: packageInfo.packageName || packageInfo.name || "Unknown Package",
            package_id: packageInfo.package_id || packageInfo.id,
          }));
          acc.push(...reviewsWithPackageInfo);
        }
        return acc;
      }, []);

      processReviewsData(allReviews);
    } catch (error) {
      console.error("Error loading package reviews:", error);
      setReviewsError("Failed to load reviews: " + error.message);
      setReviews([]);
    } finally {
      setReviewsLoading(false);
    }
  }, [fetchOperatorPackages, fetchPackageReviews, processReviewsData]);

  // Memoized UI components
  const renderStars = useCallback((rating, size = "normal") => {
    const starSize = size === "large" ? "24px" : "16px";
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`star ${index < rating ? "filled" : "empty"}`}
        style={{ fontSize: starSize }}
      >
        {index < rating ? "★" : "☆"}
      </span>
    ));
  }, []);

  const CategoryRating = useCallback(({ label, rating }) => (
    <div className="category-rating">
      <span className="category-label">{label}:</span>
      <div className="category-stars">
        {renderStars(safeRound(rating))}
        <span className="category-score">({safeToFixed(rating)})</span>
      </div>
    </div>
  ), [renderStars, safeRound, safeToFixed]);

  const ReviewCard = useCallback(({ review }) => (
    <div className="review-card">
      <div className="review-header">
        <div className="customer-info">
          <div className="customer-avatar">
            <div className="avatar-placeholder">
              {review.user_id?.charAt(0).toUpperCase() || "U"}
            </div>
          </div>
          <div className="customer-details">
            <h4 className="customer-name">
              User {review.user_id || "Anonymous"}
            </h4>
            <div className="review-meta">
              <span className="review-date">
                {safeFormatDate(review.created_at)}
              </span>
              <span className="package-name">
                {review.package_name}
              </span>
            </div>
          </div>
        </div>
        <div className="review-rating">
          {renderStars(safeRound(review.rating_overall))}
          <span className="rating-text">
            {safeToFixed(review.rating_overall)}/5
          </span>
        </div>
      </div>

      <div className="category-ratings-individual">
        {[
          { key: 'itinerary', label: 'Itinerary', value: review.rating_itinerary },
          { key: 'value', label: 'Value', value: review.rating_value },
          { key: 'safety', label: 'Safety', value: review.rating_safety }
        ].map(({ key, label, value }) => 
          value && (
            <div key={key} className="category-rating-item">
              <span>{label}:</span>
              {renderStars(safeRound(value))}
              <span>({safeToFixed(value)})</span>
            </div>
          )
        )}
      </div>

      <div className="review-content">
        <p className="review-comment">
          {review.comment || "No comment provided"}
        </p>
      </div>
    </div>
  ), [renderStars, safeRound, safeToFixed, safeFormatDate]);

  // Memoized render sections
  const calculatePercentage = useCallback((count) => {
    return reviewStats.total > 0 ? (count / reviewStats.total) * 100 : 0;
  }, [reviewStats.total]);

  const renderRatingDistribution = useMemo(() => (
    <div className="rating-distribution">
      <h4>Rating Distribution</h4>
      {[5, 4, 3, 2, 1].map((rating) => (
        <div key={rating} className="distribution-row">
          <span className="rating-label">{rating} ★</span>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{
                width: `${calculatePercentage(reviewStats.ratingDistribution[rating])}%`,
              }}
            ></div>
          </div>
          <span className="rating-count">
            ({reviewStats.ratingDistribution[rating]})
          </span>
        </div>
      ))}
    </div>
  ), [reviewStats.ratingDistribution, calculatePercentage]);

  const renderCategoryRatings = useMemo(() => (
    <div className="category-ratings">
      <h4>Category Ratings</h4>
      <CategoryRating label="Itinerary" rating={reviewStats.categoryAverages.itinerary} />
      <CategoryRating label="Value for Money" rating={reviewStats.categoryAverages.value} />
      <CategoryRating label="Safety" rating={reviewStats.categoryAverages.safety} />
      <CategoryRating label="Overall" rating={reviewStats.categoryAverages.overall} />
    </div>
  ), [reviewStats.categoryAverages, CategoryRating]);

  const renderReviewsList = useMemo(() => {
    if (reviewsLoading) {
      return (
        <div className="loading-reviews">
          <div className="loading-spinner"></div>
          <p>Loading reviews...</p>
        </div>
      );
    }

    if (reviewsError) {
      return (
        <div className="reviews-error">
          <p>{reviewsError}</p>
          <button onClick={loadReviews} className="retry-btn">
            Retry
          </button>
        </div>
      );
    }

    if (reviews.length === 0) {
      return (
        <div className="no-reviews">
          <div className="no-reviews-icon">⭐</div>
          <h4>No Reviews Yet</h4>
          <p>
            You haven't received any reviews for your packages yet. They will
            appear here once customers start reviewing your packages.
          </p>
        </div>
      );
    }

    return (
      <div className="reviews-container">
        {reviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>
    );
  }, [reviewsLoading, reviewsError, reviews, loadReviews, ReviewCard]);

  // Load reviews on mount
  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  return (
    <div className="reviews-section">
      <div className="reviews-header">
        <h2>Package Reviews</h2>
        <p>Manage and respond to customer feedback for your packages</p>
      </div>

      {/* Reviews Overview */}
      <div className="reviews-overview">
        <div className="overview-card">
          <div className="rating-score">
            <div className="average-rating">{reviewStats.averageRating}</div>
            <div className="stars-large">
              {renderStars(safeRound(reviewStats.averageRating), "large")}
            </div>
            <div className="total-reviews">{reviewStats.total} reviews</div>
          </div>
        </div>

        <div className="rating-details">
          {renderCategoryRatings}
          {renderRatingDistribution}
        </div>
      </div>

      {/* Reviews List */}
      <div className="reviews-list">
        <div className="reviews-list-header">
          <h3>All Package Reviews ({reviewStats.total})</h3>
          <div className="sort-options">
            <select>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Rated</option>
              <option value="lowest">Lowest Rated</option>
            </select>
          </div>
        </div>

        {renderReviewsList}
      </div>
    </div>
  );
};

export default React.memo(Reviews);